import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@mui/material";
import { StyledContainer } from "Components/Auth/Styles";
import { UnstyledLink } from "Components/Routes/UnstyledLink";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { FormPasswordInput } from "Components/Shared/Inputs/Form/FormPasswordInput";
import { SignInStep } from "State/Auth/Models/AuthStateModels";
import { Resources, useResource } from "Translations/Resources";
import { nameof } from "Utils/ObjectUtils";
import { getAppUrl } from "Utils/UrlUtils";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { type ObjectSchema, object, string } from "yup";

const COMPONENT_RESOURCES = Resources.SignIn.Form;

type Props = {
  isLoading: boolean;
  serverError: string | null;
  step: SignInStep;
  login: string;
  onSubmit: (formData: FormData) => void;
  onReset: () => void;
};

const StyledForm = styled.form`
  margin-top: ${(props) => props.theme.spacing(2)};
`;

const StyledSubmitButton = styled(PrimaryButton)`
  margin-top: ${(props) => props.theme.spacing(3)};
`;

const ForgottenPassword = styled(UnstyledLink)`
  display: flex;
  justify-content: end;
  font-weight: 600;
  font-size: 14px;
  margin-top: 8px;
  margin-bottom: 8px;
`;

export type FormData = {
  login: string;
  password: string;
  token?: string;
};

export const SignInForm = ({
  isLoading,
  serverError,
  step,
  login,
  onReset,
  onSubmit,
}: Props) => {
  const { t } = useResource();

  const validationSchema: ObjectSchema<FormData> = object({
    login: string().required(t(Resources.Validation.Required)),
    password: string().required(t(Resources.Validation.Required)),
    token: string().when(nameof<FormData>("login"), ([_], schema) =>
      step === SignInStep.VerificationToken
        ? schema.required(t(Resources.Validation.Required))
        : schema.notRequired(),
    ),
  }).defined();

  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: { login: login, password: "", token: "" },
  });

  useEffect(() => {
    if (serverError) {
      setError("password", {
        type: "manual",
        message: serverError,
      });
    }
  }, [serverError, setError]);

  const appUrl = getAppUrl();

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <StyledContainer>
        <div>
          {step === SignInStep.Credentials && (
            <>
              <FormInput
                autoFocus={login.length === 0}
                control={control}
                name="login"
                errors={errors}
                label={t(COMPONENT_RESOURCES.Fields.Email)}
                fullWidth
                valueTransformer={(v) => v.toLowerCase()}
              />
              <FormPasswordInput
                autoFocus={login.length > 0}
                control={control}
                name="password"
                label={t(COMPONENT_RESOURCES.Fields.Password)}
                autoComplete="current-password"
                disableHelperText
              />
              <ForgottenPassword to={appUrl("forgottenPassword")}>
                {t(COMPONENT_RESOURCES.Controls.ForgottenPassword)}
              </ForgottenPassword>
              {errors.password && (
                <ErrorText fontWeight={400}>
                  {errors.password.message}
                </ErrorText>
              )}
            </>
          )}

          {step === SignInStep.VerificationToken && (
            <FormInput
              autoFocus
              control={control}
              name="token"
              errors={errors}
              label="Verification token"
            />
          )}

          <StyledSubmitButton
            type="submit"
            fullWidth
            variant="contained"
            color="info"
            isLoading={isLoading}
          >
            {t(COMPONENT_RESOURCES.Controls.SignInButtonLabel)}
          </StyledSubmitButton>

          {step === SignInStep.VerificationToken && (
            <Button
              onClick={() => onReset()}
              type="reset"
              fullWidth
              variant="text"
              color="primary"
              disabled={isLoading}
            >
              {t(COMPONENT_RESOURCES.Controls.CancelButtonLabel)}
            </Button>
          )}
        </div>
      </StyledContainer>
    </StyledForm>
  );
};
